import { useAuth, useOrganizationList } from '@clerk/clerk-react'
import type { ReactNode } from 'react'
import { Navigate, useParams, useSearchParams } from 'react-router-dom'
import type { DeepLinkIntent } from '@/lib/deep-link-intent.util'
import {
  buildDeepLinkChatUrl,
  extractPrefillValues,
  saveDeepLinkIntent,
} from '@/lib/deep-link-intent.util'
import { rootRoutes } from '@/routes/org-routes.const'

const buildIntentFromUrl = (agencySlug: string, searchParams: URLSearchParams): DeepLinkIntent => {
  const prefillValues = extractPrefillValues(searchParams)

  return {
    agencySlug,
    templateId: searchParams.get('t') ?? undefined,
    prompt: searchParams.get('prompt') ?? undefined,
    prefillValues: Object.keys(prefillValues).length > 0 ? prefillValues : undefined,
    timestamp: Date.now(),
  }
}

export const DeepLinkPage = (): ReactNode => {
  const { agencySlug } = useParams<{ agencySlug: string }>()
  const [searchParams] = useSearchParams()
  const { isLoaded, isSignedIn } = useAuth()
  const { userMemberships } = useOrganizationList({
    userMemberships: { infinite: true },
  })

  if (!agencySlug) {
    return <Navigate to="/" replace />
  }

  if (!isLoaded) {
    return null
  }

  const intent = buildIntentFromUrl(agencySlug, searchParams)

  if (!isSignedIn) {
    saveDeepLinkIntent(intent)
    return <Navigate to={`/${rootRoutes.signUp}`} state={{ from: '/' }} replace />
  }

  // Signed-in: resolve org and redirect directly to chat URL
  if (!userMemberships || userMemberships.isLoading || !userMemberships.data) {
    return null
  }

  const firstOrg = userMemberships.data[0]
  if (firstOrg?.organization?.slug) {
    return <Navigate to={buildDeepLinkChatUrl(firstOrg.organization.slug, intent)} replace />
  }

  // No org yet — save intent for after onboarding
  saveDeepLinkIntent(intent)
  return <Navigate to={`/${rootRoutes.onboarding}`} replace />
}
